import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, ArrowUpRight, Home, Eye, EyeOff, Package, RotateCcw, Shirt } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import UserHeader from '@/components/UserHeader';
import GarmentItemDetailModal from '@/components/GarmentItemDetailModal';
import GarmentDispatchModal from '@/components/GarmentDispatchModal';
import GarmentAddStockModal from '@/components/GarmentAddStockModal';
import GarmentReturnModal from '@/components/GarmentReturnModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 50;

const GarmentDepartment = () => {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddStock, setShowAddStock] = useState(false);
  const [showDispatch, setShowDispatch] = useState(false);
  const [showReturn, setShowReturn] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDiscontinued, setShowDiscontinued] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  
  const fetchedRef = useRef(false);
  const lastFetchParamsRef = useRef('');

  const fetchInventory = async (pageNum = 1, discontinued = false, append = false) => {
    const fetchKey = `${pageNum}-${discontinued}`;
    
    if (fetchKey === lastFetchParamsRef.current && !append) {
      return;
    }
    
    try {
      if (!append) setLoading(true);
      
      const response = await axios.get(
        `${API}/garment/inventory?include_discontinued=${discontinued}&page=${pageNum}&page_size=${PAGE_SIZE}`
      );
      
      const data = response.data;
      lastFetchParamsRef.current = fetchKey;
      
      if (append) {
        setInventory(prev => [...prev, ...data.items]);
      } else {
        setInventory(data.items);
      }
      setTotal(data.total);
      setHasMore(data.has_more);
      setPage(pageNum);
      setLoading(false);
    } catch (error) {
      console.error('[GARMENT] Fetch error:', error);
      toast.error('Failed to load garment inventory');
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/garment/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      console.error('[GARMENT] Dashboard error:', error);
    }
  };

  useEffect(() => {
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      fetchInventory(1, showDiscontinued);
      fetchDashboard();
    }
  }, []);

  const handleToggleDiscontinued = () => {
    const newValue = !showDiscontinued;
    setShowDiscontinued(newValue);
    lastFetchParamsRef.current = '';
    fetchInventory(1, newValue);
  };

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      fetchInventory(page + 1, showDiscontinued, true);
    }
  };

  const handleRefresh = () => {
    lastFetchParamsRef.current = '';
    fetchedRef.current = false;
    fetchInventory(1, showDiscontinued);
    fetchDashboard();
  };

  const filteredInventory = useMemo(() => {
    if (!searchQuery.trim()) return inventory;
    
    const query = searchQuery.toLowerCase();
    return inventory.filter(item =>
      item.fabric_name?.toLowerCase().includes(query) ||
      item.fabric_code?.toLowerCase().includes(query) ||
      item.qr_code?.toLowerCase().includes(query)
    );
  }, [inventory, searchQuery]);

  const handleItemClick = (item) => {
    setSelectedItem(item);
  };

  const handleCloseDetail = () => {
    setSelectedItem(null);
    handleRefresh();
  };

  const handleDispatchComplete = () => {
    setShowDispatch(false);
    handleRefresh();
  };

  const handleAddStockComplete = () => {
    setShowAddStock(false);
    handleRefresh();
  };

  const handleReturnComplete = () => {
    setShowReturn(false);
    handleRefresh();
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-12">
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        <div className="sticky top-0 bg-slate-50 pb-6 pt-4 z-10">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                data-testid="home-button"
                className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-slate-100 transition-colors"
              >
                <Home className="w-5 h-5 text-slate-600" />
              </button>
              <div>
                <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
                  Garment Department
                </h1>
                <p className="text-sm text-slate-500">Stitched Samples</p>
                <p className="text-sm text-slate-500 font-mono">Measured in Pieces</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowAddStock(true)}
                data-testid="add-stock-button"
                className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 shadow-md shadow-emerald-600/20 transition-colors font-medium"
              >
                <Plus className="w-5 h-5" />
                Add Stock
              </button>
              <button
                onClick={() => setShowDispatch(true)}
                data-testid="dispatch-button"
                className="flex items-center gap-2 px-4 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 shadow-md shadow-amber-600/20 transition-colors font-medium"
              >
                <ArrowUpRight className="w-5 h-5" />
                Dispatch
              </button>
              <button
                onClick={() => setShowReturn(true)}
                data-testid="return-button"
                className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-md shadow-blue-600/20 transition-colors font-medium"
              >
                <RotateCcw className="w-5 h-5" />
                Return
              </button>
              <UserHeader />
            </div>
          </div>

          {/* Dashboard Summary */}
          {dashboard && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Total Items</p>
                <p className="text-2xl font-bold text-slate-800">{dashboard.total_items}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Available</p>
                <p className="text-2xl font-bold text-emerald-600">{dashboard.total_available}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Outside</p>
                <p className="text-2xl font-bold text-amber-600">{dashboard.total_outside}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Today Dispatch</p>
                <p className="text-2xl font-bold text-blue-600">{dashboard.today_dispatch?.total || 0}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Low Stock</p>
                <p className="text-2xl font-bold text-red-600">{dashboard.low_stock_count}</p>
              </div>
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by QR Code, Fabric Name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-input"
              className="w-full pl-12 h-12 text-base rounded-full border border-slate-200 shadow-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
            />
          </div>

          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Showing {filteredInventory.length} of {total} items
            </p>
            <button
              onClick={handleToggleDiscontinued}
              data-testid="toggle-discontinued"
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                showDiscontinued
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {showDiscontinued ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              {showDiscontinued ? 'Showing All' : 'Show Discontinued'}
            </button>
          </div>
        </div>

        {/* Inventory Grid */}
        {loading && inventory.length === 0 ? (
          <div className="flex justify-center items-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-4 border-amber-600 border-t-transparent rounded-full animate-spin" />
              <p className="text-slate-500">Loading garments...</p>
            </div>
          </div>
        ) : filteredInventory.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-500">
            <Shirt className="w-16 h-16 mb-4 text-slate-300" />
            <p className="text-lg font-medium">No garments found</p>
            <p className="text-sm">Garment items are created automatically when TL items are added</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredInventory.map((item) => (
                <GarmentCard
                  key={item.id}
                  item={item}
                  onClick={() => handleItemClick(item)}
                />
              ))}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={handleLoadMore}
                  disabled={loading}
                  className="px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium disabled:opacity-50"
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {selectedItem && (
        <GarmentItemDetailModal
          item={selectedItem}
          onClose={handleCloseDetail}
          onRefresh={handleRefresh}
        />
      )}

      {showDispatch && (
        <GarmentDispatchModal
          onClose={() => setShowDispatch(false)}
          onComplete={handleDispatchComplete}
        />
      )}

      {showAddStock && (
        <GarmentAddStockModal
          onClose={() => setShowAddStock(false)}
          onComplete={handleAddStockComplete}
        />
      )}

      {showReturn && (
        <GarmentReturnModal
          onClose={() => setShowReturn(false)}
          onComplete={handleReturnComplete}
        />
      )}
    </div>
  );
};

// Garment Card Component
const GarmentCard = ({ item, onClick }) => {
  const isLowStock = item.total_available <= item.min_level;
  const isOutOfStock = item.total_available === 0;

  return (
    <div
      onClick={onClick}
      data-testid={`garment-card-${item.id}`}
      className={`bg-white rounded-2xl border-2 overflow-hidden cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] ${
        isOutOfStock
          ? 'border-red-300 bg-red-50/50'
          : isLowStock
          ? 'border-amber-300 bg-amber-50/50'
          : 'border-slate-200'
      }`}
    >
      {/* Photo */}
      <div className="aspect-[4/3] bg-slate-100 relative">
        {item.photo_url ? (
          <img
            src={`${BACKEND_URL}${item.photo_url}`}
            alt={item.fabric_name}
            className="w-full h-full object-contain"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Shirt className="w-16 h-16 text-slate-300" />
          </div>
        )}
        
        {/* Status Badge */}
        {(isOutOfStock || isLowStock) && (
          <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-bold ${
            isOutOfStock ? 'bg-red-500 text-white' : 'bg-amber-500 text-white'
          }`}>
            {isOutOfStock ? 'OUT OF STOCK' : 'LOW STOCK'}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4">
        <h3 className="font-semibold text-slate-800 truncate">{item.fabric_name}</h3>
        <p className="text-xs text-slate-500 font-mono mb-3">{item.qr_code || item.fabric_code}</p>
        
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-emerald-50 rounded-lg p-2">
            <p className="text-lg font-bold text-emerald-600">{item.total_available}</p>
            <p className="text-[10px] text-emerald-700 uppercase">Available</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-2">
            <p className="text-lg font-bold text-amber-600">{item.total_outside}</p>
            <p className="text-[10px] text-amber-700 uppercase">Outside</p>
          </div>
          <div className="bg-slate-100 rounded-lg p-2">
            <p className="text-lg font-bold text-slate-600">{item.total_pieces}</p>
            <p className="text-[10px] text-slate-700 uppercase">Total</p>
          </div>
        </div>

        {/* Garment Types Preview */}
        {item.garment_types && item.garment_types.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {item.garment_types.slice(0, 3).map((gt, idx) => (
              <span key={idx} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] rounded-full">
                {gt.garment_type}: {gt.quantity_available}
              </span>
            ))}
            {item.garment_types.length > 3 && (
              <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] rounded-full">
                +{item.garment_types.length - 3} more
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GarmentDepartment;
